import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getSocket, joinRoom, sendMessage, createGeneralRoom, createPrivateRoom } from '../services/socket';
import api, { chatAPI } from '../api/client';
import webSocketEventBus from '../services/webSocketEventBus';
import { User, Activity, Pill, AlertTriangle, Heart, ChevronRight, Shield, Calendar, Send, Clock, CheckCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ChatRoom() {
  const { symptomId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [rooms, setRooms] = useState([]);
  const [currentRoomId, setCurrentRoomId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [symptom, setSymptom] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [roomUnreadCounts, setRoomUnreadCounts] = useState({});
  const [countsLoaded, setCountsLoaded] = useState(false);
  const [autoSelectionCleared, setAutoSelectionCleared] = useState(false);
  const [patientContext, setPatientContext] = useState(null);
  const [patientSymptoms, setPatientSymptoms] = useState([]);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [accessRequestStatus, setAccessRequestStatus] = useState(null); // null | 'pending' | 'sent' | 'existing'
  const [consentScope, setConsentScope] = useState(null); // null = no restriction
  
  const endRef = useRef(null);
  const isDoctor = user?.role === 'DOCTOR';
  
  // Get roomId from URL query parameter if provided
  // Get roomId from URL query parameter if provided
  const urlRoomId = searchParams.get('roomId');

  // Load available rooms and select default
  useEffect(() => {
    if (!user || !symptomId) return;
    
    const loadRooms = async () => {
      try {
        setLoading(true);
        
        // Try to fetch rooms from backend first
        let availableRooms = [];
        try {
          const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
          const roomsResponse = await fetch(`${API_URL}/chat/symptom/${symptomId}/rooms`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('symptoconnect_token')}` }
          });
          
          if (roomsResponse.ok) {
            const data = await roomsResponse.json();
            availableRooms = data.rooms || [];
            console.log('✅ Loaded rooms from backend:', availableRooms);
          } else {
            throw new Error('Failed to fetch rooms');
          }
        } catch (err) {
          
          // Fallback: Create default rooms based on user role
          if (user.role === 'USER') {
            // Patient: NO general room, empty list (private rooms only appear after doctor messages)
            availableRooms = [];
            console.log('📋 Using fallback rooms (USER - empty, waiting for doctor to initiate):', availableRooms);
          } else if (user.role === 'DOCTOR') {
            // Doctor: create their private room with the patient
            availableRooms = [{
              roomId: createPrivateRoom(symptomId, user.id),
              name: `Private Chat (Dr. ${user.name})`,
              type: 'private',
              doctorId: user.id
            }];
            console.log('📋 Using fallback rooms (DOCTOR):', availableRooms);
          }
          console.log('📋 Using fallback rooms:', availableRooms);
        }
        
        setRooms(availableRooms);
        
        // Check if a specific roomId was requested via URL parameter
        if (urlRoomId && availableRooms.find(r => r.roomId === urlRoomId)) {
          const selectedRoom = availableRooms.find(r => r.roomId === urlRoomId);
          setCurrentRoomId(urlRoomId);
          
          console.log('🔗 Room selected from URL:', urlRoomId, 'type:', selectedRoom?.type);
          // Clear notifications if it's a private room
          if (selectedRoom?.type === 'private') {
            console.log('🧹 Clearing notifications for URL-selected room:', urlRoomId);
            webSocketEventBus.emit('clearNotificationsByRoomId', urlRoomId);
          }
        } 
        // Otherwise, auto-select appropriate room based on user role
        else if (availableRooms.length > 0) {
          let defaultRoom;
          if (user.role === 'USER') {
            // Patient: prefer general room, fallback to first private room
            defaultRoom = availableRooms.find(r => r.type === 'general') || availableRooms[0];
          } else {
            // Doctor: use their private room
            defaultRoom = availableRooms.find(r => r.type === 'private' && r.doctorId === user.id) || availableRooms[0];
          }
          
          if (defaultRoom) {
            console.log('🎯 Auto-selecting default room:', defaultRoom.roomId, defaultRoom.name);
            setCurrentRoomId(defaultRoom.roomId);
            // Notification clearing will be handled by the separate useEffect after counts load
          } else {
            console.log('⚠️ No default room found to auto-select');
          }
        }
        
      } catch (err) {
        console.error('Failed to load chat rooms:', err);
        setError('Failed to load chat rooms. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadRooms();
  }, [user, symptomId, urlRoomId]);

  // Clear all notifications for this symptom when entering the chat
  useEffect(() => {
    if (symptomId) {
      webSocketEventBus.emit('clearSymptomNotifications', symptomId);
    }
  }, [symptomId]);

  // Listen for notification changes and update unread counts
  useEffect(() => {
    const handleNotificationChange = (counts) => {
      console.log('📬 ChatRoom received notificationsChanged:', counts);
      setRoomUnreadCounts(counts || {});
      setCountsLoaded(true); // Mark that we've received counts
    };
    
    webSocketEventBus.on('notificationsChanged', handleNotificationChange);
    
    // Request initial counts immediately when component mounts
    console.log('📞 ChatRoom requesting initial notification counts...');
    webSocketEventBus.emit('requestNotificationCounts');
    
    return () => {
      webSocketEventBus.off('notificationsChanged', handleNotificationChange);
    };
  }, []);

  // Clear notification for auto-selected room AFTER counts are loaded (only once)
  useEffect(() => {
    console.log('🔍 Auto-clear effect - countsLoaded:', countsLoaded, 'currentRoomId:', currentRoomId, 'rooms.length:', rooms.length, 'autoSelectionCleared:', autoSelectionCleared);
    console.log('🔍 roomUnreadCounts:', roomUnreadCounts);
    
    if (!countsLoaded || !currentRoomId || !rooms.length || autoSelectionCleared) {
      console.log('⏭️ Skipping auto-clear - conditions not met');
      return;
    }
    
    const currentRoom = rooms.find(r => r.roomId === currentRoomId);
    console.log('🔍 Current room found:', currentRoom);
    
    // Only clear if this is a private room with unread count
    if (currentRoom?.type === 'private' && roomUnreadCounts[currentRoomId] > 0) {
      console.log('🧹 Clearing notifications for auto-selected room (after counts loaded):', currentRoomId);
      webSocketEventBus.emit('clearNotificationsByRoomId', currentRoomId);
      setAutoSelectionCleared(true); // Mark as done so it doesn't repeat
    } else {
      console.log('⏭️ Not clearing - room type:', currentRoom?.type, 'unread count:', roomUnreadCounts[currentRoomId]);
    }
  }, [countsLoaded, currentRoomId, rooms, roomUnreadCounts, autoSelectionCleared]);

  // Load patient context for doctors
  useEffect(() => {
    if (!isDoctor || !symptom?.userId) return;
    const patientId = symptom.userId._id || symptom.userId;

    // Fetch patient profile (may fail if consent not granted — that's OK)
    api.get(`/patient-profile/${patientId}`).then(res => {
      const scope = res?.data?.consentScope || null;
      setConsentScope(scope);
      setPatientContext(res?.data?.data || res?.data || null);

      // Only fetch other symptoms if scope allows
      if (!scope || scope.symptoms !== false) {
        api.get(`/symptoms?patientId=${patientId}&limit=10`).then(res2 => {
          const data = res2?.data?.data || res2?.data || [];
          setPatientSymptoms(Array.isArray(data) ? data.filter(s => s._id !== symptomId).slice(0, 5) : []);
        }).catch(() => {});
      }
    }).catch(() => setPatientContext(null));
  }, [isDoctor, symptom?.userId, symptomId]);

  // Load symptom details
  useEffect(() => {
    if (!symptomId) return;
    
    const loadSymptom = async () => {
      try {
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
        const response = await fetch(`${API_URL}/symptoms/${symptomId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('symptoconnect_token')}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Backend returns { symptom: {...} }, so extract the symptom object
          const symptomData = data.symptom || data;
          setSymptom(symptomData);
        } else {
          console.error('❌ Failed to load symptom, status:', response.status);
        }
      } catch (err) {
        console.error('Failed to load symptom:', err);
      }
    };
    
    loadSymptom();
  }, [symptomId]);

  // Listen for real-time symptom status updates
  useEffect(() => {
    if (!symptomId) return;

    const rootSocket = getSocket(); // Root namespace for general events
    if (!rootSocket) return;

    const handleStatusUpdate = (data) => {
      console.log('📝 ChatRoom: Symptom status update received:', data);
      // Update the symptom status if it matches the current symptom
      if (data.id === symptomId) {
        setSymptom(prev => prev ? { ...prev, status: data.status } : null);
      }
    };

    rootSocket.on('symptomStatusUpdate', handleStatusUpdate);

    return () => {
      rootSocket.off('symptomStatusUpdate', handleStatusUpdate);
    };
  }, [symptomId]);

  // Load chat history and connect to room
  useEffect(() => {
    if (!currentRoomId) return;
    
    const initializeChat = async () => {
      try {
        // Load chat history
        const historyResponse = await chatAPI.getHistory(currentRoomId);
        setMessages(historyResponse.data.messages || []);
        
        // Socket connection is managed by AuthContext — only join the room here.
        const chat = getSocket('chat');

        joinRoom(currentRoomId, (resp) => {
          if (!resp?.ok) {
            console.error('joinRoom failed:', resp?.error);
            setError(`Failed to join room: ${resp?.error}`);
          } else {
            console.log('✅ Successfully joined room:', currentRoomId);
          }
        });

        // Listen for new messages
        const onMsg = (m) => {
          console.log('💬 ChatRoom received message:', m);

          if (m?.roomId === currentRoomId) {
            // Normalize message format
            // Backend sends: { sender: { id, role, name }, text, createdAt, roomId }
            // But display expects: { senderRole, text, createdAt }
            const normalizedMessage = {
              ...m,
              id: m.id || `${m.sender?.id}-${m.createdAt}-${m.text?.substring(0, 10)}`,
              senderRole: m.sender?.role || m.senderRole,
              senderId: m.sender?.id || m.senderId,
              senderName: m.sender?.name || m.senderName
            };

            console.log('💬 Normalized message:', normalizedMessage);

            // Prevent duplicates
            setMessages((prev) => {
              const isDuplicate = prev.some(existing =>
                (existing.id && existing.id === normalizedMessage.id) ||
                (existing.text === normalizedMessage.text &&
                 (existing.senderId === normalizedMessage.senderId || existing.sender?.id === normalizedMessage.senderId) &&
                 Math.abs(new Date(existing.createdAt).getTime() - new Date(normalizedMessage.createdAt).getTime()) < 1000)
              );

              if (isDuplicate) {
                console.log('⚠️ Duplicate message prevented');
                return prev;
              }
              return [...prev, normalizedMessage];
            });
            // Auto-dismiss notifications since user is viewing this chat (outside updater — side effects must not be in state updaters)
            webSocketEventBus.emit('clearSymptomNotifications', symptomId);
          }
        };
        chat?.on?.('message', onMsg);

        return () => {
          chat?.off?.('message', onMsg);
        };
        
      } catch (err) {
        console.error('Failed to initialize chat:', err);
        setError('Failed to load chat history');
      }
    };

    initializeChat();
  }, [currentRoomId, symptomId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const onSend = () => {
    if (!text.trim() || !currentRoomId) return;
    
    sendMessage(currentRoomId, text, (resp) => {
      if (!resp?.ok) {
        console.error('message failed:', resp?.error);
        const errorMessage = resp?.error || 'Failed to send message';
        setError(errorMessage);
        
        // Auto-dismiss error after 5 seconds
        setTimeout(() => {
          setError('');
        }, 5000);
      } else {
        setText('');
        setError('');
      }
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    if (!symptomId || !user || user.role !== 'DOCTOR') return;
    
    if (newStatus === 'CLOSED') {
      const confirmed = window.confirm('Are you sure you want to close this symptom? The patient will no longer be able to send messages in the main chat.');
      if (!confirmed) return;
    }
    
    try {
      setUpdatingStatus(true);
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
      const response = await fetch(`${API_URL}/symptoms/${symptomId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('symptoconnect_token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        const updatedSymptom = await response.json();
        setSymptom(updatedSymptom);
        setError('');
      } else {
        throw new Error('Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-9 w-9 border-2 border-outline-variant/30 border-t-primary-600 mx-auto mb-3"></div>
          <p className="text-sm text-surface-500">Loading chat...</p>
        </div>
      </div>
    );
  }

  if (!currentRoomId && rooms.length === 0) {
    return (
      <div className="max-w-lg mx-auto p-6 text-center">
        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-10">
          <svg className="mx-auto h-14 w-14 text-surface-300 mb-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
          <h2 className="text-lg font-bold text-surface-900 mb-1">No Chat Rooms Available</h2>
          <p className="text-sm text-surface-500 mb-6">No chat rooms are available for this symptom yet.</p>
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-surface-100 text-surface-700 text-sm font-semibold rounded-xl hover:bg-surface-200 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/></svg>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentRoom = rooms.find(r => r.roomId === currentRoomId);

  // Patient context sidebar for doctors
  const PatientSidebar = () => {
    if (!isDoctor || !symptom?.userId) return null;
    const patient = symptom.userId;
    const profile = patientContext;
    const riskLevel = profile?.currentRisk?.overall || 0;
    const riskColor = riskLevel >= 70 ? 'text-error' : riskLevel >= 40 ? 'text-amber-600' : 'text-emerald-600';
    const riskBg = riskLevel >= 70 ? 'bg-error/10' : riskLevel >= 40 ? 'bg-amber-50' : 'bg-emerald-50';
    const conditions = profile?.conditions?.filter(c => c.status === 'active') || [];
    const medications = profile?.medications?.filter(m => m.status === 'active') || [];
    const dob = profile?.demographics?.dateOfBirth;
    const age = dob ? Math.floor((Date.now() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000)) : null;
    const gender = profile?.demographics?.gender;

    return (
      <div className="space-y-4">
        {/* Patient card */}
        <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-sm text-slate-900 truncate">{patient.name || 'Patient'}</h3>
              <p className="text-xs text-slate-500 truncate">{patient.email}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {age != null && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-50 text-slate-600">
                <Calendar className="w-3 h-3" />{age}y
              </span>
            )}
            {gender && gender !== 'prefer_not_to_say' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-50 text-slate-600 capitalize">
                {gender}
              </span>
            )}
            {riskLevel > 0 && (
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg font-semibold ${riskBg} ${riskColor}`}>
                <Shield className="w-3 h-3" />Risk {riskLevel}%
              </span>
            )}
          </div>
          <button
            onClick={() => navigate(`/patient/${patient._id || patient}`)}
            className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-primary bg-primary/5 hover:bg-primary/10 rounded-xl transition-colors"
          >
            View Full Profile <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Active conditions */}
        {conditions.length > 0 && (!consentScope || consentScope.conditions !== false) && (
          <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-5">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Heart className="w-3.5 h-3.5" /> Conditions
            </h4>
            <div className="space-y-2">
              {conditions.slice(0, 5).map((c) => (
                <div key={c._id || c.name} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700 truncate">{c.name}</span>
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                    c.severity === 'severe' ? 'bg-error/10 text-error' :
                    c.severity === 'moderate' ? 'bg-amber-50 text-amber-600' :
                    'bg-emerald-50 text-emerald-600'
                  }`}>{c.severity}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active medications */}
        {medications.length > 0 && (!consentScope || consentScope.medications !== false) && (
          <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-5">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Pill className="w-3.5 h-3.5" /> Medications
            </h4>
            <div className="space-y-2">
              {medications.slice(0, 5).map((m) => (
                <div key={m._id || m.name}>
                  <span className="text-sm font-medium text-slate-700">{m.name}</span>
                  <span className="text-xs text-slate-500 ml-1">{m.dosage} · {m.frequency}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No profile data fallback — request consent */}
        {!profile && conditions.length === 0 && medications.length === 0 && (
          <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-5 text-center">
            <AlertTriangle className="w-5 h-5 text-amber-400 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Detailed profile data requires patient consent.</p>

            {accessRequestStatus === 'sent' || accessRequestStatus === 'existing' ? (
              <div className="mt-3 space-y-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-xs font-semibold">
                  {accessRequestStatus === 'sent' ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                  {accessRequestStatus === 'sent' ? 'Request sent!' : 'Request already pending'}
                </div>
                <button
                  onClick={() => navigate('/care-requests')}
                  className="block mx-auto text-xs font-semibold text-primary hover:underline flex items-center gap-1 justify-center"
                >
                  View request status <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                disabled={requestingAccess}
                onClick={async () => {
                  const patientId = patient._id || patient;
                  setRequestingAccess(true);
                  try {
                    // Check if request already exists
                    const checkRes = await api.get(`/care-requests/check-existing/${patientId}`);
                    if (checkRes.data?.hasExisting) {
                      setAccessRequestStatus('existing');
                      toast('You already have a pending request for this patient', { icon: '⏳' });
                      return;
                    }
                    // Send care request
                    await api.post('/care-requests/send', {
                      patientId,
                      message: `Dr. ${user?.name || 'Your doctor'} would like to access your health data to provide better care.`,
                      triggeredBySymptomId: symptomId
                    });
                    setAccessRequestStatus('sent');
                    toast.success('Care request sent! Patient will be notified.');
                  } catch (err) {
                    const msg = err.response?.data?.error || 'Failed to send request';
                    if (msg.includes('already have a pending')) {
                      setAccessRequestStatus('existing');
                    }
                    toast.error(msg);
                  } finally {
                    setRequestingAccess(false);
                  }
                }}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50"
              >
                {requestingAccess ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-3 h-3" /> Request access</>
                )}
              </button>
            )}
          </div>
        )}

        {/* Recent symptoms */}
        {patientSymptoms.length > 0 && (!consentScope || consentScope.symptoms !== false) && (
          <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-5">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" /> Other Symptoms
            </h4>
            <div className="space-y-2">
              {patientSymptoms.map(s => (
                <button
                  key={s._id}
                  onClick={() => navigate(`/symptom/${s._id}`)}
                  className="w-full text-left flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm text-slate-700 truncate">{s.title}</span>
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                    s.status === 'OPEN' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'
                  }`}>{s.status}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`mx-auto px-4 py-5 sm:py-8 ${isDoctor ? 'max-w-7xl' : 'max-w-4xl'}`}>
      {/* Header */}
      <div className="mb-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-extrabold text-surface-900 tracking-tight break-words">
            {symptom?.title || `Symptom #${symptomId?.slice(-6)}`}
          </h1>
          <p className="text-sm text-surface-500 mt-0.5">
            {currentRoom ? `${currentRoom.name} · ${currentRoom.type === 'private' ? 'Private' : 'Group'}` : 'Select a room'}
          </p>
          <div className="mt-2 flex items-center gap-2">
            {symptom ? (
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg ${
                symptom.status === 'OPEN'
                  ? 'bg-teal-50 text-teal-700'
                  : 'bg-surface-100 text-surface-600'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${symptom.status === 'OPEN' ? 'bg-teal-500' : 'bg-surface-400'}`}></span>
                {symptom.status === 'OPEN' ? 'Active' : 'Closed'}
              </span>
            ) : (
              <span className="text-xs px-2.5 py-1 rounded-lg bg-surface-100 text-surface-400 animate-pulse">Loading...</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {user?.role === 'DOCTOR' && (
            <div className="flex gap-1.5">
              <button
                onClick={() => handleStatusChange('OPEN')}
                disabled={!symptom || updatingStatus || symptom.status === 'OPEN'}
                className={`px-3.5 py-2 text-xs font-semibold rounded-xl transition-all ${
                  symptom?.status === 'OPEN'
                    ? 'bg-teal-500 text-white'
                    : 'bg-surface-100 text-surface-600 hover:bg-teal-50 hover:text-teal-600'
                } disabled:opacity-50`}
              >
                {updatingStatus && symptom?.status !== 'OPEN' ? '...' : 'Open'}
              </button>
              <button
                onClick={() => handleStatusChange('CLOSED')}
                disabled={!symptom || updatingStatus || symptom.status === 'CLOSED'}
                className={`px-3.5 py-2 text-xs font-semibold rounded-xl transition-all ${
                  symptom?.status === 'CLOSED'
                    ? 'bg-surface-700 text-white'
                    : 'bg-surface-100 text-surface-600 hover:bg-danger-50 hover:text-danger-600'
                } disabled:opacity-50`}
              >
                {updatingStatus && symptom?.status !== 'CLOSED' ? '...' : 'Close'}
              </button>
            </div>
          )}
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-surface-100 text-surface-600 text-xs font-semibold rounded-xl hover:bg-surface-200 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/></svg>
            Back
          </button>
        </div>
      </div>

      {/* Two-column layout: Chat + Patient sidebar (doctors only) */}
      <div className={`${isDoctor ? 'lg:grid lg:grid-cols-[1fr_320px] lg:gap-6' : ''}`}>
      {/* Left column: Chat */}
      <div className="min-w-0">

      {/* Room Selection */}
      {rooms.length > 1 && (
        <div className="mb-4 bg-white rounded-2xl border border-outline-variant/10 shadow-premium p-3.5">
          <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wider mb-2.5">Chat Rooms</label>
          <div className="flex gap-2 flex-wrap">
            {rooms.map(room => {
              const unreadCount = roomUnreadCounts[room.roomId] || 0;
              return (
                <button
                  key={room.roomId}
                  onClick={() => {
                    setCurrentRoomId(room.roomId);
                    if (room.type === 'private') {
                      webSocketEventBus.emit('clearNotificationsByRoomId', room.roomId);
                    }
                  }}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all ${
                    room.roomId === currentRoomId
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-surface-50 text-surface-600 border border-outline-variant/20 hover:border-primary-300 hover:text-primary-700'
                  }`}
                >
                  {room.name}
                  {room.type === 'private' && (
                    <svg className="w-3 h-3 opacity-70" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
                  )}
                  {unreadCount > 0 && (
                    <span className="ml-0.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-danger-500 rounded-full">{unreadCount}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-danger-50 border border-danger-100 rounded-2xl flex items-start gap-3">
          <svg className="w-5 h-5 text-danger-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
          <p className="flex-1 text-sm text-danger-700 font-medium">{error}</p>
          <button onClick={() => setError('')} className="text-danger-400 hover:text-danger-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
      )}

      {/* Chat Messages */}
      <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-premium overflow-hidden">
        <div className="h-[calc(100vh-380px)] min-h-[300px] max-h-[600px] overflow-y-auto p-5 space-y-3 bg-surface-50">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <svg className="h-14 w-14 text-surface-300 mb-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm font-semibold text-surface-600">No messages yet</p>
              <p className="text-xs text-surface-400 mt-1">Start the conversation!</p>
            </div>
          ) : (
            messages.map((m) => {
              const isMine = (m.senderId || m.sender?.id) === user?.id;
              return (
                <div key={m._id || m.createdAt} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] sm:max-w-md px-4 py-2.5 rounded-2xl ${
                    isMine
                        ? 'bg-primary text-white rounded-br-md'
                        : 'bg-white text-surface-900 border border-outline-variant/10 rounded-bl-md shadow-sm'
                  }`}>
                    <div className={`text-xs font-medium mb-1 ${isMine ? 'text-primary-200' : 'text-surface-400'}`}>
                      {m.senderName || (m.senderRole === 'DOCTOR' ? 'Doctor' : 'Patient')} &middot; {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className="text-sm leading-relaxed">{m.text}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>
        
        {/* Message Input */}
        <div className="border-t border-surface-100 p-4">
          {symptom?.status === 'CLOSED' && !currentRoomId.includes('_doctor_') && (
            <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-700 text-xs font-medium flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
              This symptom is closed. Please reopen it to continue the conversation.
            </div>
          )}
          <div className="flex gap-2.5">
            <input 
              value={text} 
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-surface-container-highest border border-outline-variant/20 rounded-xl px-4 py-2.5 text-sm text-surface-900 placeholder-surface-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all" 
              placeholder={
                (symptom?.status === 'CLOSED' && !currentRoomId.includes('_doctor_')) 
                  ? 'Symptom is closed...' 
                  : 'Type a message...'
              }
              disabled={!currentRoomId || (symptom?.status === 'CLOSED' && !currentRoomId.includes('_doctor_'))}
            />
            <button 
              onClick={onSend} 
              disabled={!text.trim() || !currentRoomId || (symptom?.status === 'CLOSED' && !currentRoomId.includes('_doctor_'))}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary to-primary-container text-white text-sm font-semibold rounded-full disabled:bg-surface-200 disabled:text-surface-400 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-elevated"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"/></svg>
              <span className="hidden sm:inline">Send</span>
            </button>
          </div>
        </div>
      </div>

      </div>{/* end left column */}

      {/* Right column: Patient context (desktop only for doctors) */}
      {isDoctor && (
        <div className="hidden lg:block">
          <PatientSidebar />
        </div>
      )}
      </div>{/* end grid */}

      {/* Mobile patient context (collapsible, doctors only) */}
      {isDoctor && (
        <details className="lg:hidden mt-4">
          <summary className="cursor-pointer text-sm font-semibold text-primary bg-primary/5 hover:bg-primary/10 rounded-xl px-4 py-3 transition-colors">
            Patient Context
          </summary>
          <div className="mt-3">
            <PatientSidebar />
          </div>
        </details>
      )}
    </div>
  );
}
